<script setup>
import { defineProps, onMounted, ref, defineEmits, watch, computed } from "vue";
import Email from "@/components/form-fields/email/Index.vue";
import FirstName from "@/components/form-fields/first-name/Index.vue";
import LastName from "@/components/form-fields/last-name/Index.vue";
import Phone from "@/components/form-fields/phone/Index.vue";
import { usePhoneAreaCodes } from '@/hooks/common/usePhoneAreaCodes';

import { useCustomer } from '@/hooks/shopify-hooks/useCustomer';
import { 
	useCsrFormValidator,
} from "@/hooks/common/useCsrFormValidator";
import { isPhonePortrait } from '@/hooks/common/useUtils';
import { _CSR_IS_B2b, _CSR_LOGIN_EMAIL } from '@/const/shopifyConst.js';
import { useGetStorage, useSetStorage } from '@/hooks/common/useStorage';

const props = defineProps({
  stateKey: {
    type: String,
    default: "",
  },
  config: {
    type: Object,
    default: null
  },
  customer: {
    type: Object,
    default: null
  }
});

const emailInput = ref(null);
const firstNameInput = ref(null);
const lastNameInput = ref(null);
const phoneNumberInput = ref(null);

const areaCodesVal = ref('');

const showPhoneNav = ref(false);
const isB2b = ref(false);
const isLoading = ref(false);

isB2b.value = useGetStorage(_CSR_IS_B2b) === true;
console.log('isB2b', isB2b.value);

const emit = defineEmits(['close', 'cancel']);
const { areaCodes,  load: loadAreaCodes } = usePhoneAreaCodes();

// 自己控制 Phone 组件的渲染时机，确保 areaCodesVal 设置完毕后再渲染
// 不能直接用 areaCodesLoaded，因为 load() 内部先设 loaded=true 再 return，
// 导致 Phone 组件创建时 areaCodesVal 还没被赋值
const phoneReady = ref(false);

// 回显时移除已带区号的前缀，避免与区号选择器/显示区号重复（如 +86 +86xxxxxxxx）
const phoneValue = computed(() => {
  const full = props.customer?.phone || '';
  if (!full) {
    return 
  };
  const codes = areaCodes.value;
  console.log('areaCodes',areaCodes)
  if (!codes.length) {
    return full
  };
  // 按长度降序匹配，避免 +8 误匹配 +84 这类前缀包含的情况
  // 单区号和多区号统一处理：只要手机号以某个配置区号开头就去掉
  const sorted = [...codes].sort((a, b) => b.length - a.length);
  const matched = sorted.find((code) => code && full.startsWith(code));
  return matched ? full.slice(matched.length).replace(/^\s+/, '') : full;
});




onMounted(async () => {
  showPhoneNav.value = isPhonePortrait();
  const codes = await loadAreaCodes();
  if (!codes.length) {
    phoneReady.value = true;
    return;
  }
  const full = (props.customer?.phone || '').trim();
  if (codes.length === 1) {
    // 单区号：直接用唯一的区号，确保提交时不重复拼接
    areaCodesVal.value = codes[0];
  } else {
    // 多个区号：从 customer.phone 中匹配正确区号
    if (full) {
      const sorted = [...codes].sort((a, b) => b.length - a.length);
      const matched = sorted.find((code) => code && full.startsWith(code));
      if (matched) {
        areaCodesVal.value = matched;
      }
    }
  }
  // areaCodesVal 已设置完毕，现在才允许 Phone 组件渲染
  phoneReady.value = true;
})
const getAreaCodes = (val) =>{
  areaCodesVal.value = val
}
const submitForm = async () => {
  useCsrFormValidator([emailInput, firstNameInput, lastNameInput, phoneNumberInput]).then(async (res) => {
    const { email, firstName, lastName, phone } = res;
    console.log('phone1111',phone)
    const { updateCustomer } = useCustomer();
    try {
      // 更新customer
      isLoading.value = true;
      const res = await updateCustomer({
        firstName,
        lastName,
        email,
        // E.164 国际格式：区号 + 手机号（仅去除空格）
        phone: phone ? `${areaCodesVal.value}${String(phone).replace(/\s/g, '')}` : ''
      }, '',props.config)
      isLoading.value = false;
      console.log('res--->',res)
    
      if (res?.customerUserErrors?.length > 0) {
        // const errorTxt = res?.customerUserErrors[0].message;
      
        emit('loading', false);
      } else {
        useSetStorage(_CSR_LOGIN_EMAIL, email);
        // 派发事件给iservice，更新email
        window?.observer?.dispatch("email-js-loaded", email);
        emit('loading', false);
        if(res){
          emit('close');
        }
      }
    } catch(e) {
      console.error(e);
      isLoading.value = false;
    }
  });
}

const clickCancel = () => {
  emit('cancel');
}


watch(() => isLoading.value, (value) => {
  emit('loading', value);
})


</script>

<template>
  <div :class="{'form-v3-ecommerce': true, 'csr-account-form': true}">
    <div class="csr-account-form__nav-title" v-if="showPhoneNav" @click="() => clickCancel()">
      <div class="csr-account__back__icon"></div>
      {{ config.personInfo.title }}
    </div>
    <form @submit.prevent="submitForm">
      <div class="csr-account-form__field">
        <FirstName
          ref="firstNameInput"
          :data-conf="{ 
            label: config.personInfo.firstnameLabel, 
            placeholder: config.personInfo.firstnamePlaceholder, 
            required: true,
            name: 'firstName',
            defaultValue: customer.firstName,
            defErrorMsg: config.requiredFieldsPrompt,
          }"
        />
        <LastName
          ref="lastNameInput"   
          :data-conf="{ 
            label: config.personInfo.lastnameLabel, 
            placeholder: config.personInfo.lastnamePlaceholder, 
            required: true,
            name: 'lastName',
            defaultValue: customer.lastName,
            defErrorMsg: config.requiredFieldsPrompt,
          }" 
        />
      </div>
      <div class="csr-account-form__field--email">
        <Email
          ref="emailInput"
          :data-conf="{ 
            label: config.personInfo.emailLabel, 
            placeholder: config.personInfo.emailPlaceholder, 
            required: true,
            name: 'email',
            defaultValue: customer.email,
            defErrorMsg: config.requiredFieldsPrompt,
            formatHint: config.personInfo.emailFormatPrompt,
            disabled: true, // isB2b,
          }"
        />
      </div>
      <div class="csr-account-form__field--phone">
     
          <!-- :data-conf="{ 
            label: config.address.phoneLabel, 
            placeholder: config.address.phonePlaceholder, 
            required: isMx ? true: false,
            name: 'phone',
            defaultValue: editAddress?.phone,
            defErrorMsg: config.requiredFieldsPrompt,
          }" -->
          <Phone
          v-if="phoneReady"
          @getAreaCodes="getAreaCodes"
          ref="phoneNumberInput"
          :data-conf="{
            placeholder: config.personInfo.phonePlaceholder, 
            label: config.personInfo.phoneLabel,
             name: 'phone',
             defaultValue: phoneValue,
              areaCodes: areaCodes,
              initialAreaCode: areaCodesVal,
          }"
        />

      </div>
      <div class="csr-account-form__btn-group">
        <button class="csr-account-form__btn md-button-v2 md-button md-button--primary" type="submit">{{ config.confirmButton }}</button> 
        <button class="csr-account-form__btn md-button-v2 md-button md-button--black" type="button" @click="() => clickCancel()">{{ config.cancelButton }}</button> 
      </div>
    </form>
  </div>
</template>

